<!-- Reusable metric grid: takes a metrics array from the server -->
<ng-template #metricGrid let-metrics="metrics">
  <div class="metrics-wrap">
    <div class="metric-card" *ngFor="let m of metrics"
         [class.metric-suppressed]="m.status === 'suppressed'"
         [title]="metricTooltip(m)">
      <div class="metric-label">{{ m.displayName }}</div>
      <div class="metric-value">{{ formatMetricValue(m) }}</div>
      <div class="metric-suppressed-note" *ngIf="m.status === 'suppressed'">Suppressed</div>
    </div>
  </div>
</ng-template>

<!-- Reusable breakdown row -->
<ng-template #breakdownRow let-entries="entries" let-heading="heading">
  <div class="breakdown-section" *ngIf="entries.length">
    <h4>{{ heading }}</h4>
    <div class="metrics-wrap">
      <div class="metric-card metric-card-small" *ngFor="let e of entries">
        <div class="metric-label">{{ e.label }}</div>
        <div class="metric-value">{{ formatCount(e.value) }}</div>
      </div>
    </div>
  </div>
</ng-template>

<div class="page-container">
  <h2>Video Analytics</h2>
  <p class="subtitle">Fetch analytics for a specific post, reel, video or story.</p>

  <div class="form-section">
    <!-- Platform select -->
    <div class="form-group">
      <label for="platform">Platform</label>
      <select id="platform" [(ngModel)]="selectedPlatform" (ngModelChange)="onPlatformChange()">
        <option value="">-- Select platform --</option>
        <option *ngFor="let p of platforms" [value]="p">{{ p | titlecase }}</option>
      </select>
    </div>

    <!-- Creator select -->
    <div class="form-group" *ngIf="selectedPlatform">
      <label for="creator">Creator Account</label>
      <select id="creator" [(ngModel)]="selectedOAuthId" (ngModelChange)="onCreatorChange()"
              [disabled]="filteredOAuths.length === 0">
        <option value="">-- Select creator --</option>
        <option *ngFor="let o of filteredOAuths" [value]="o.id">{{ o.username }}</option>
      </select>
      <span class="hint" *ngIf="filteredOAuths.length === 0">
        No {{ selectedPlatform | titlecase }} accounts connected.
      </span>
    </div>

    <!-- Instagram: posts/reels are found by link, stories have to be picked from the live list -->
    <div class="form-group" *ngIf="selectedOAuthId && isInstagram">
      <label>Content</label>
      <div class="mode-tabs">
        <button class="mode-tab" [class.mode-tab-active]="contentMode === 'post'"
                (click)="setContentMode('post')">Post / Reel</button>
        <button class="mode-tab" [class.mode-tab-active]="contentMode === 'story'"
                (click)="setContentMode('story')">Active Stories</button>
      </div>
    </div>

    <!-- Content ID / link + submit -->
    <div class="form-group" *ngIf="selectedOAuthId && contentMode === 'post'">
      <label for="videoId">{{ contentIdLabel }}</label>
      <div class="input-row">
        <input id="videoId" type="text" [(ngModel)]="videoId" [placeholder]="contentIdPlaceholder" />
        <button class="btn-fetch" (click)="onSubmit()" [disabled]="loading || !videoId.trim()">
          {{ loading ? 'Fetching…' : 'Fetch Analytics' }}
        </button>
      </div>
      <span class="hint" *ngIf="isInstagram">
        Paste the post or reel link. A long number copied out of an instagram.com URL is Instagram's
        internal ID and cannot be used.
      </span>
    </div>

    <!-- Timeframe — only shown for platforms that support it -->
    <div class="form-group" *ngIf="selectedOAuthId && timeframeSupported && contentMode === 'post'">
      <label for="timeframe">Timeframe</label>
      <select id="timeframe" [(ngModel)]="selectedTimeframeDays">
        <option *ngFor="let t of timeframes" [ngValue]="t.days">{{ t.label }}</option>
      </select>
    </div>
  </div>

  <!-- Error -->
  <div class="error-box" *ngIf="errorMessage">
    {{ errorMessage }}
  </div>

  <!-- ── Post / reel / video results ─────────────────────────────────────────── -->
  <div class="results-section" *ngIf="result && contentMode === 'post'">
    <div class="results-header">
      <h3>
        Results for <em>{{ result.videoId }}</em>
        <span class="type-chip" *ngIf="result.mediaProductType">{{ result.mediaProductType }}</span>
      </h3>
      <div class="cache-row" *ngIf="lastRefresh">
        <span class="cache-label" [class.cache-fresh]="!fromCache">
          {{ fromCache ? 'Cached' : 'Live' }} — fetched {{ lastRefreshAgo() }}
        </span>
        <button class="btn-refresh" (click)="onSubmit(true)" [disabled]="loading">
          Refresh
        </button>
      </div>
    </div>

    <!-- Instagram: the server sends exactly the metrics that exist for this media type -->
    <ng-container *ngIf="metricsDrivenByServer">
      <ng-container *ngTemplateOutlet="metricGrid; context: { metrics: result.metrics || [] }"></ng-container>
      <ng-container *ngTemplateOutlet="breakdownRow;
        context: { entries: navigationEntries(result), heading: 'Navigation' }"></ng-container>
      <ng-container *ngTemplateOutlet="breakdownRow;
        context: { entries: profileActivityEntries(result), heading: 'Profile Activity' }"></ng-container>
    </ng-container>

    <!-- TikTok / YouTube: fixed grid plus their platform-specific extras -->
    <div class="metrics-wrap" *ngIf="!metricsDrivenByServer">
      <div class="metric-card" title="Total lifetime views for this video">
        <div class="metric-label">Views</div>
        <div class="metric-sublabel" *ngIf="result.platform === 'youtube'">(All Time)</div>
        <div class="metric-value">{{ result.views | number }}</div>
      </div>
      <div class="metric-card" title="Total likes on this video">
        <div class="metric-label">Likes</div>
        <div class="metric-sublabel" *ngIf="result.platform === 'youtube'">(All Time)</div>
        <div class="metric-value">{{ result.likes | number }}</div>
      </div>
      <div class="metric-card" title="Total comments on this video">
        <div class="metric-label">Comments</div>
        <div class="metric-sublabel" *ngIf="result.platform === 'youtube'">(All Time)</div>
        <div class="metric-value">{{ result.comments | number }}</div>
      </div>
      <div class="metric-card" title="Total times this video was shared">
        <div class="metric-label">Shares</div>
        <div class="metric-sublabel" *ngIf="result.platform === 'youtube'">({{ selectedTimeframeLabel }})</div>
        <div class="metric-value">{{ result.shares | number }}</div>
      </div>
      <div class="metric-card" *ngIf="result.platform !== 'tiktok' || result.reach > 0"
           title="Unique accounts that saw this video (excludes rewatches)">
        <div class="metric-label">Reach</div>
        <div class="metric-value">{{ result.reach | number }}</div>
      </div>
      <div class="metric-card" *ngIf="result.platform !== 'tiktok' || result.saves > 0"
           title="Times this video was bookmarked or saved">
        <div class="metric-label">Saves</div>
        <div class="metric-value">{{ result.saves | number }}</div>
      </div>
      <div class="metric-card" title="(Likes + Comments + Shares) ÷ Views × 100">
        <div class="metric-label">Engagement Rate</div>
        <div class="metric-value">{{ result.engagementRate | number:'1.2-2' }}%</div>
      </div>

      <!-- Platform-specific extended metrics -->
      <ng-container *ngFor="let m of result.metrics">
        <div class="metric-card"
             *ngIf="!(m.value === 0 && m.format === 'duration')"
             [title]="m.description">
          <div class="metric-label">{{ m.displayName }}</div>
          <div class="metric-value">{{ formatMetricValue(m) }}</div>
        </div>
      </ng-container>
    </div>

    <!-- Time-series chart -->
    <div class="chart-section" *ngIf="chartVisible">
      <div class="chart-header">
        <h4>Performance Over Time</h4>
        <select class="chart-metric-select" [(ngModel)]="selectedTimeSeriesMetric"
                (ngModelChange)="onTimeSeriesMetricChange()">
          <option *ngFor="let k of timeSeriesMetricOptions" [value]="k">
            {{ timeSeriesMetricLabel(k) }}
          </option>
        </select>
      </div>
      <div class="chart-wrapper">
        <canvas baseChart [data]="chartData" [type]="'line'" [options]="chartOptions"></canvas>
      </div>
    </div>

    <!-- Video thumbnail (click to open on platform) -->
    <div class="thumbnail-container" *ngIf="thumbnailUrl">
      <a [href]="videoUrl" target="_blank" rel="noopener">
        <img [src]="thumbnailUrl" class="video-thumbnail" alt="Video thumbnail" />
      </a>
    </div>
  </div>

  <!-- ── Active stories ──────────────────────────────────────────────────────── -->
  <div class="results-section" *ngIf="contentMode === 'story' && selectedOAuthId">
    <div class="results-header">
      <h3>Active Stories</h3>
      <div class="cache-row">
        <button class="btn-refresh" (click)="loadStories()" [disabled]="storiesLoading">
          {{ storiesLoading ? 'Loading…' : 'Reload' }}
        </button>
      </div>
    </div>

    <p class="chart-note">
      Instagram only serves stories for the 24 hours they are live, and the insights are gone
      permanently once a story expires. Nothing is captured in the background yet — reload this tab
      late in a story's life to see its final numbers.
    </p>

    <div class="error-box" *ngIf="storiesError">{{ storiesError }}</div>

    <p class="hint" *ngIf="storiesLoaded && !storiesError && !storyGroups.length">
      This creator has no live stories right now.
    </p>

    <!-- Story picker -->
    <div class="story-picker" *ngIf="storyGroups.length">
      <button class="story-chip" *ngFor="let g of storyGroups"
              [class.story-chip-active]="selectedStoryGroup?.groupId === g.groupId"
              (click)="selectStoryGroup(g)">
        <span class="story-chip-title">{{ storyGroupLabel(g) }}</span>
        <span class="story-chip-sub">{{ expiresIn(g) }}</span>
      </button>
    </div>

    <ng-container *ngIf="selectedStoryGroup as group">
      <!-- Story totals -->
      <h4 class="section-heading">Story Totals</h4>
      <div class="metrics-wrap">
        <div class="metric-card" title="Frame views added up across the whole story">
          <div class="metric-label">Total Views</div>
          <div class="metric-value">{{ formatCount(group.totalViews) }}</div>
        </div>
        <div class="metric-card"
             title="Reach counts an account once per frame, so it cannot be summed — this is the largest single-frame reach, a lower bound for the story">
          <div class="metric-label">Reach</div>
          <div class="metric-sublabel">(max frame)</div>
          <div class="metric-value">{{ formatCount(group.maxFrameReach) }}</div>
        </div>
        <div class="metric-card" title="Replies across all frames">
          <div class="metric-label">Replies</div>
          <div class="metric-value">{{ formatCount(group.totalReplies) }}</div>
        </div>
        <div class="metric-card" title="Shares across all frames">
          <div class="metric-label">Shares</div>
          <div class="metric-value">{{ formatCount(group.totalShares) }}</div>
        </div>
        <div class="metric-card" title="Profile visits driven by this story">
          <div class="metric-label">Profile Visits</div>
          <div class="metric-value">{{ formatCount(group.totalProfileVisits) }}</div>
        </div>
        <div class="metric-card" title="New follows from this story">
          <div class="metric-label">New Follows</div>
          <div class="metric-value">{{ formatCount(group.totalFollows) }}</div>
        </div>
        <div class="metric-card" title="Total interactions across all frames">
          <div class="metric-label">Interactions</div>
          <div class="metric-value">{{ formatCount(group.totalInteractions) }}</div>
        </div>
        <div class="metric-card" *ngIf="group.frames.length > 1"
             title="Last frame views ÷ first frame views">
          <div class="metric-label">Completion</div>
          <div class="metric-value">{{ formatRatio(group.completion) }}</div>
        </div>
      </div>

      <p class="hint" *ngIf="failedFrameCount(group) as failed">
        {{ failed }} of {{ group.frames.length }} frames returned no insights — see the per-frame table.
      </p>

      <!-- Drop-off chart -->
      <div class="chart-section" *ngIf="group.frames.length > 1">
        <div class="chart-header">
          <h4>Frame-to-Frame Drop-off</h4>
        </div>
        <div class="chart-wrapper">
          <canvas baseChart [data]="storyChartData" [type]="'line'" [options]="chartOptions"></canvas>
        </div>
      </div>

      <!-- Per-frame table -->
      <h4 class="section-heading">Frames</h4>
      <div class="frame-table">
        <div class="frame-row frame-head">
          <span>Frame</span>
          <span>Posted</span>
          <span>Views</span>
          <span>Reach</span>
          <span>vs. first</span>
          <span>vs. prev</span>
          <span>Exits</span>
          <span>Exit rate</span>
        </div>
        <ng-container *ngFor="let f of group.frames; let i = index">
          <div class="frame-row" [class.frame-row-active]="selectedStoryFrame?.mediaId === f.mediaId"
               (click)="selectStoryFrame(f)">
            <span>
              <img class="frame-thumb" *ngIf="f.thumbnailUrl" [src]="f.thumbnailUrl" alt="" />
              {{ i + 1 }}
              <span class="frame-type">{{ f.mediaType }}</span>
            </span>
            <span>{{ formatTime(f.postedAt) }}</span>
            <span>{{ formatCount(frameViews(f)) }}</span>
            <span>{{ f.analytics?.success ? formatCount(f.analytics.reach) : '—' }}</span>
            <span>{{ formatRatio(f.retentionFromFirst) }}</span>
            <span>{{ formatRatio(f.retentionFromPrevious) }}</span>
            <span>{{ formatCount(f.exits) }}</span>
            <span>{{ formatRatio(f.exitRate) }}</span>
          </div>

          <!-- Expanded frame detail -->
          <div class="frame-detail" *ngIf="selectedStoryFrame?.mediaId === f.mediaId">
            <div class="error-box" *ngIf="!f.analytics?.success">
              {{ f.analytics?.message || 'Instagram returned no insights for this frame.' }}
            </div>

            <ng-container *ngIf="f.analytics?.success">
              <ng-container *ngTemplateOutlet="metricGrid;
                context: { metrics: f.analytics.metrics || [] }"></ng-container>
              <ng-container *ngTemplateOutlet="breakdownRow;
                context: { entries: navigationEntries(f.analytics), heading: 'Navigation' }"></ng-container>
              <ng-container *ngTemplateOutlet="breakdownRow;
                context: { entries: profileActivityEntries(f.analytics), heading: 'Profile Activity' }"></ng-container>
            </ng-container>

            <div class="thumbnail-container" *ngIf="f.thumbnailUrl">
              <a [href]="f.permalink || f.thumbnailUrl" target="_blank" rel="noopener">
                <img [src]="f.thumbnailUrl" class="video-thumbnail" alt="Story frame" />
              </a>
            </div>
          </div>
        </ng-container>
      </div>
    </ng-container>
  </div>
</div>
